import axios from 'axios'

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://adserver.martimart.com/api/v1'

// Debug logging to see what URL is actually being used
console.log('🔍 API Configuration Debug:')
console.log('  VITE_API_URL env var:', import.meta.env.VITE_API_URL)
console.log('  Final API_BASE_URL:', API_BASE_URL)

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  
  // Debug logging for all API requests
  console.log('🌐 API Request:', {
    method: config.method?.toUpperCase(),
    url: config.url,
    baseURL: config.baseURL,
    fullURL: `${config.baseURL}${config.url}`
  })
  
  return config
})

// Types
export interface StoredTitle {
  id: number
  text: string
  createdAt: string
  is_favorite?: boolean
  usage_count?: number
  approach?: string
}

export interface StoredHeadline {
  id: number
  text: string
  createdAt: string
  is_favorite?: boolean
  usage_count?: number
  style?: string
  length?: string
}

export interface Client {
  id: string
  name: string
  email: string
  role: string
  brand: string
  meta_account_id: string
  page_id: string
  page_name: string
  pixel_id: string
  instagram_account_id?: string
  default_budget: number
  default_duration: number
  default_countries: string[]
  default_age_min: number
  default_age_max: number
  default_genders: number[]
  primary_color: string
  secondary_color: string
  website?: string
  industry?: string
  utm_template?: string
  // Default Messenger Settings
  default_messenger_greeting?: string
  default_messenger_quick_replies?: Array<{title: string; payload: string}>
  message_creative_id?: string
  created_at: string
  createdAt: string
  updatedAt: string
}

export interface Asset {
  id: string
  filename: string
  originalName: string
  type: string
  size: number
  url: string
  createdAt: string
}

export interface Campaign {
  id: string
  name: string
  status: string
  budget: number
  objective: string
  createdAt: string
  updatedAt: string
}

export interface CampaignInstruction {
  name: string
  description: string
  template: string
}

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  logout: () =>
    api.post('/auth/logout'),
  
  getCurrentUser: () =>
    api.get('/auth/me'),
}

// Assets API
export const assetsApi = {
  upload: (formData: FormData, onUploadProgress?: (progressEvent: any) => void) =>
    api.post('/assets/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    }),
  
  getAll: () =>
    api.get('/assets'),
  
  getUserAssets: (clientId: number, limit?: number, offset?: number, page?: number, sortBy?: string, sortOrder?: string, typeFilter?: string) =>
    api.get(`/assets?client_id=${clientId}&limit=${limit || 20}&offset=${offset || 0}${page ? `&page=${page}` : ''}${sortBy ? `&sort_by=${sortBy}` : ''}${sortOrder ? `&sort_order=${sortOrder}` : ''}${typeFilter ? `&type_filter=${typeFilter}` : ''}`),

  getAssetsByIds: (clientId: number, assetIds: number[]) =>
    api.post(`/assets/by-ids`, { client_id: clientId, asset_ids: assetIds }),
  
  getById: (id: string) =>
    api.get(`/assets/${id}`),
  
  delete: (id: string) =>
    api.delete(`/assets/${id}`),
  
  getByType: (type: string) =>
    api.get(`/assets?type=${type}`),
  
  getApprovedVariants: (clientId: number) =>
    api.get(`/assets/approved-variants?client_id=${clientId}`),
  
  importMetaAssets: (clientId: number, assetIds: string[], assetTypes: string[]) =>
    api.post('/assets/meta/import', { client_id: clientId, asset_ids: assetIds, asset_types: assetTypes }),
  
  reimportMetaAssets: (clientId: number) =>
    api.post(`/assets/meta/reimport/${clientId}`),
  
  syncMetaAssets: (clientId: number, batchSize: number = 10, offset: number = 0) =>
    api.get(`/assets/meta/sync/${clientId}?batch_size=${batchSize}&offset=${offset}`),
  
  generateVariants: (variantRequest: any) =>
    api.post('/assets/generate-variants', variantRequest),
  
  getVariantSet: (id: string) =>
    api.get(`/assets/variants/${id}`),
  
  approveVariants: (variantSetId: string, selectedVariants: any[]) =>
    api.post(`/assets/variants/${variantSetId}/approve`, selectedVariants),
  
  updateVariant: (variantId: number, variantData: any) =>
    api.put(`/assets/variants/${variantId}`, variantData),
  
  deleteVariant: (variantId: number) =>
    api.delete(`/assets/variants/${variantId}`),
  
  deleteVariantSet: (variantSetId: number) =>
    api.delete(`/assets/variant-sets/${variantSetId}`),
}

// Content API
export const contentApi = {
  generateTitles: (prompt: string) =>
    api.post('/content/generate-titles', { prompt }),
  
  generateHeadlines: (prompt: string) =>
    api.post('/content/generate-headlines', { prompt }),
  
  generateContent: (prompt: string, type: string) =>
    api.post('/content/generate', { prompt, type }),
  
  getStoredTitles: () =>
    api.get('/content/titles'),
  
  getStoredHeadlines: () =>
    api.get('/content/headlines'),
  
  getTitles: (clientId: number, favoritesOnly?: boolean) =>
    api.get(`/content/titles?client_id=${clientId}&favorites=${favoritesOnly || false}`),
  
  getHeadlines: (clientId: number, favoritesOnly?: boolean) =>
    api.get(`/content/headlines?client_id=${clientId}&favorites=${favoritesOnly || false}`),
  
  bulkSaveContent: (contentData: any) =>
    api.post('/content/bulk-save', contentData),
  
  incrementTitleUsage: (titleId: number) =>
    api.post(`/content/titles/${titleId}/use`),
  
  incrementHeadlineUsage: (headlineId: number) =>
    api.post(`/content/headlines/${headlineId}/use`),
}

// Campaigns API
export const campaignsApi = {
  create: (campaignData: any) =>
    api.post('/campaigns', campaignData),
  
  getAll: () =>
    api.get('/campaigns'),
  
  getById: (id: string) =>
    api.get(`/campaigns/${id}`),
  
  update: (id: string, campaignData: any) =>
    api.put(`/campaigns/${id}`, campaignData),
  
  delete: (id: string) =>
    api.delete(`/campaigns/${id}`),
  
  launch: (campaignData: any) =>
    api.post('/campaigns/launch', campaignData),
  
  launchMultiple: (campaignData: any) =>
    api.post('/campaigns/launch-multiple', campaignData),
  
  createAdvanced: (campaignData: any) =>
    api.post('/campaigns/create-advanced', campaignData),
  
  pause: (id: string) =>
    api.post(`/campaigns/${id}/pause`),
  
  resume: (id: string) =>
    api.post(`/campaigns/${id}/resume`),
  
  getTypes: () =>
    api.get('/campaigns/types'),
  
  getInstructions: () =>
    api.get('/ai-creative/campaign-instructions'),

  getMetaCampaigns: (clientId: number) =>
    api.get(`/campaigns/meta-campaigns?client_id=${clientId}`),

  addToExisting: (data: any) =>
    api.post('/campaigns/add-to-existing', data),
}

// Clients API
export const clientsApi = {
  getAll: (limit?: number, offset?: number, page?: number) =>
    api.get(`/clients?limit=${limit || 20}&offset=${offset || 0}${page ? `&page=${page}` : ''}`),
  
  getClients: (limit?: number, offset?: number, page?: number) =>
    api.get(`/clients?limit=${limit || 20}&offset=${offset || 0}${page ? `&page=${page}` : ''}`),
  
  getById: (id: string) =>
    api.get(`/clients/${id}`),
  
  create: (clientData: any) =>
    api.post('/clients', clientData),
  
  createClient: (clientData: any) =>
    api.post('/clients', clientData),
  
  update: (id: string, clientData: any) =>
    api.put(`/clients/${id}`, clientData),
  
  updateClient: (id: string, clientData: any) =>
    api.put(`/clients/${id}`, clientData),
  
  delete: (id: string) =>
    api.delete(`/clients/${id}`),
  
  deleteClient: (id: string) =>
    api.delete(`/clients/${id}`),
  
  getPresets: () =>
    api.get('/clients/presets'),
  
  createPreset: (presetData: any) =>
    api.post('/clients/presets', presetData),
  
  updatePreset: (id: string, presetData: any) =>
    api.put(`/clients/presets/${id}`, presetData),
  
  deletePreset: (id: string) =>
    api.delete(`/clients/presets/${id}`),
}

// AI/Generation Functions
export const getCampaignInstructions = async () => {
  try {
    const response = await campaignsApi.getInstructions()
    return {
      data: {
        success: response.data?.success ?? true,
        campaign_types: response.data?.campaign_types || {}
      }
    }
  } catch (error) {
    console.error('Error fetching campaign instructions:', error)
    return {
      data: {
        success: false,
        campaign_types: {}
      }
    }
  }
}

export const generateContentWithSearch = async (params: any): Promise<any> => {
  try {
    const formData = new FormData()
    formData.append('prompt', params.prompt)
    formData.append('campaign_type', params.campaign_type || 'soft_sale')
    if (params.search_query) formData.append('search_query', params.search_query)
    formData.append('include_search', String(params.include_search ?? true))
    const response = await api.post('/ai-creative/generate-content-with-search', formData, {
      headers: { 'Content-Type': undefined }
    })
    return { data: response.data }
  } catch (error) {
    console.error('Error generating content with search:', error)
    throw error
  }
}

export const generateImageWithStyle = async (params: any): Promise<any> => {
  try {
    const formData = new FormData()
    formData.append('prompt', params.prompt)
    formData.append('campaign_type', params.campaign_type || 'soft_sale')
    formData.append('size', params.size || '1024x1024')
    if (params.client_id) formData.append('client_id', String(params.client_id))
    const response = await api.post('/ai-creative/generate-image-with-style', formData, {
      headers: { 'Content-Type': undefined }
    })
    return { data: response.data }
  } catch (error) {
    console.error('Error generating image with style:', error)
    throw error
  }
}

export const generateTitlesHeadlines = async (params: any) => {
  try {
    const formData = new FormData()
    formData.append('product_info', params.product_info)
    formData.append('target_audience', params.target_audience)
    formData.append('brand_context', params.brand_context || '')
    formData.append('num_titles', String(params.num_titles || 5))
    formData.append('num_headlines', String(params.num_headlines || 5))
    const response = await api.post('/ai-creative/generate-titles-headlines', formData, {
      headers: { 'Content-Type': undefined }
    })
    const body = response.data
    const content = body.generated_content || body
    return {
      data: {
        titles: content.titles || body.titles || [],
        headlines: content.headlines || body.headlines || []
      }
    }
  } catch (error) {
    console.error('Error generating titles and headlines:', error)
    throw error
  }
}

// Analytics API
export const analyticsApi = {
  getAnalytics: (clientId: number, datePreset: string = 'yesterday', limit: number = 10, offset: number = 0, sortBy: string = 'spend', sortOrder: string = 'desc') =>
    api.get(`/analytics-fast/?client_id=${clientId}&date_preset=${datePreset}&limit=${limit}&offset=${offset}&sort_by=${sortBy}&sort_order=${sortOrder}`),
  
  updateCampaignStatus: (campaignId: string, status: string) =>
    api.post(`/analytics/campaigns/${campaignId}/status`, { status }),
  
  updateCampaignBudget: (campaignId: string, dailyBudget?: number, lifetimeBudget?: number) =>
    api.post(`/analytics/campaigns/${campaignId}/budget`, { 
      daily_budget: dailyBudget, 
      lifetime_budget: lifetimeBudget
    }),
  
  updateAdSetStatus: (adsetId: string, status: string) =>
    api.post(`/analytics/adsets/${adsetId}/status`, { status }),
  
  updateAdSetBudget: (adsetId: string, dailyBudget?: number, lifetimeBudget?: number) =>
    api.post(`/analytics/adsets/${adsetId}/budget`, { 
      daily_budget: dailyBudget, 
      lifetime_budget: lifetimeBudget 
    }),
  
  updateAdStatus: (adId: string, status: string) =>
    api.post(`/analytics/ads/${adId}/status`, { status }),
}

// Users API
export const usersApi = {
  getAll: () =>
    api.get('/users'),
  
  getById: (id: string) =>
    api.get(`/users/${id}`),
  
  create: (userData: any) =>
    api.post('/users', userData),
  
  update: (id: string, userData: any) =>
    api.put(`/users/${id}`, userData),
  
  delete: (id: string) =>
    api.delete(`/users/${id}`),
  
  updateRole: (id: string, role: string) =>
    api.put(`/users/${id}/role`, { role }),
}

// Investor API
export const investorApi = {
  getDashboard: (startDate: string, endDate: string) =>
    api.get(`/investor/dashboard?start_date=${startDate}&end_date=${endDate}`),

  getMe: () =>
    api.get('/investor/me'),
}

// Investor Admin API (for admin users managing investors)
export const investorAdminApi = {
  list: () =>
    api.get('/investor/admin/investors'),

  get: (id: number) =>
    api.get(`/investor/admin/investors/${id}`),

  create: (data: { email: string; password: string; name: string; profit_share_percent: number }) =>
    api.post('/investor/admin/investors', data),

  update: (id: number, data: { email?: string; password?: string; name?: string; profit_share_percent?: number }) =>
    api.put(`/investor/admin/investors/${id}`, data),

  delete: (id: number) =>
    api.delete(`/investor/admin/investors/${id}`),

  addCampaign: (investorId: number, data: {
    meta_campaign_id: string;
    meta_ad_account_id: string;
    investment_amount: number;
    label?: string;
  }) =>
    api.post(`/investor/admin/investors/${investorId}/campaigns`, data),

  updateCampaign: (investorId: number, linkId: number, data: {
    investment_amount?: number;
    label?: string;
  }) =>
    api.put(`/investor/admin/investors/${investorId}/campaigns/${linkId}`, data),

  removeCampaign: (investorId: number, linkId: number) =>
    api.delete(`/investor/admin/investors/${investorId}/campaigns/${linkId}`),
}

// Export everything
export default api